import React, { Component } from 'react';
import { ChevronDown } from 'react-feather';
import { Trans } from 'react-i18next';
import { ApiUrls } from '@api/apiUrls';
import { keyMap } from '@locales/lokalise/keyMap';
import { cookieService } from '@utils/library/cookie.service';
import { fetchApi } from 'api/fetchApi';
import cn from 'classnames';
import { GENERAL_CONSTANTS } from 'components/shared/general.constants';
import { pushAccountsBalanceCurrencyCodeEvent } from 'components/shared/googleAnalyticsEvents';
import { connectToContext, IAppContext } from 'contextStore';
import { IWallet } from 'global.interfaces';
import { handleError } from 'utils/errors';
import { getFormattedNumber } from 'utils/helper';
import styles from '../walletInfo.module.scss';

interface IWalletInfoState {
  processed: boolean;
  wallets: IWallet[];
  currentWallet: IWallet | null;
  showWalletsList: boolean;
}

class TAInfo extends Component<IAppContext, IWalletInfoState> {
  walletList: React.RefObject<HTMLDivElement>;

  constructor(props: IAppContext) {
    super(props);
    this.state = {
      processed: false,
      wallets: [],
      currentWallet: null,
      showWalletsList: false,
    };

    this.walletList = React.createRef();
  }

  componentDidMount() {
    void this.getAccountsBalance();
  }

  getAccountsBalance = async () => {
    const { i18next } = this.props;

    this.setState({ processed: true });

    try {
      const response = await fetchApi.get<IWallet[]>(`${ApiUrls.tradingAccountUser.getAccountsBalance}`);
      const { data } = response;
      const defaultWallet = cookieService.getCookie('__TAWallet') || GENERAL_CONSTANTS.DEFAULT_CURRENCY;
      const defaultWalletIndex = data.findIndex((wallet: IWallet) => wallet.currency.toUpperCase() === defaultWallet);
      const currentWallet = data[defaultWalletIndex] || data[0];

      this.setState({ currentWallet, processed: false, wallets: data });
    } catch (error) {
      handleError(i18next.t(keyMap.headerWithBalanceFailedToFetchBalance), error);
      this.setState({ processed: false });
    }
  };

  setCurrentWallet = (wallet: IWallet) => {
    pushAccountsBalanceCurrencyCodeEvent(wallet.currency);

    cookieService.setCookie('__TAWallet', wallet.currency);

    this.setState({ currentWallet: wallet, showWalletsList: false });
  };

  showWalletsList = (state?: boolean) => {
    const { showWalletsList } = this.state;
    const showState = typeof state === 'boolean' ? state : !showWalletsList;

    this.setState({ showWalletsList: showState }, () => {
      if (showState) {
        this.walletList?.current?.focus();
      }
    });
  };

  onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      this.showWalletsList(false);
    }
  };

  renderWalletsList() {
    const { wallets } = this.state;

    return (
      <div className="b-dropdown__list">
        <ul>
          {wallets.map((wallet) => (
            <li key={`${wallet.balance}_${wallet.currency}`} className="b-dropdown__item">
              <button type="button" className="b-dropdown__link" onClick={() => this.setCurrentWallet(wallet)}>
                {wallet.currency}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  render() {
    const { processed, showWalletsList, currentWallet } = this.state;

    const balance = currentWallet && currentWallet.balance ? currentWallet.balance : 0;
    const walletName = currentWallet ? currentWallet.currency : GENERAL_CONSTANTS.DEFAULT_CURRENCY;

    return (
      <div className={cn(styles.walletInfo, { 'info--loading': processed })}>
        <div className={styles.walletInfo__label}>
          <Trans i18nKey={keyMap.headerWithBalanceTABalance}>
            <span className={styles.noWrap}></span>
          </Trans>
          :
        </div>
        <div className="wallet-info__currency">
          <div className="b-dropdown custom-web__b-dropdown" ref={this.walletList} tabIndex={0} onBlur={this.onBlur}>
            <button type="button" className="b-dropdown__btn btn--small" onClick={() => this.showWalletsList()}>
              {walletName}
              <ChevronDown />
            </button>
            {showWalletsList && this.renderWalletsList()}
          </div>
        </div>
        <button type="button" className={styles.walletInfo__balance} onClick={this.getAccountsBalance}>
          <span>{getFormattedNumber(balance, { precision: 2 })}</span>
        </button>
      </div>
    );
  }
}

export default connectToContext(TAInfo);
